/**
 * The Worker terminal's own compact menu row (#515): File | Edit | View.
 *
 * It belongs to one Worker terminal, not to the app. Every item is a button with an exact
 * accessible name; a disabled item keeps its reason in its title and description so it is
 * never a silent dead end. Keyboard: Enter/Space/ArrowDown opens a menu, arrows move,
 * Home/End jump, Escape closes and returns focus to the menu button, ArrowLeft/Right move
 * between menus. Nothing here acts on the terminal itself - each item calls the handler it
 * was given.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';

export interface TerminalMenuItem {
  readonly id: string;
  readonly label: string;
  /** The exact accessible name, when the visible label alone would be ambiguous. */
  readonly ariaLabel?: string;
  readonly shortcut?: string;
  readonly disabled?: boolean;
  /** Why the item is disabled, or what it does - shown as a tooltip and announced. */
  readonly title?: string;
  readonly onSelect: () => void;
}

export interface TerminalMenu {
  readonly id: string;
  readonly label: string;
  readonly items: readonly TerminalMenuItem[];
}

function focusItem(list: HTMLElement | null, index: number): void {
  const items = [...(list?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  if (items.length === 0) return;
  const bounded = ((index % items.length) + items.length) % items.length;
  items[bounded]?.focus();
}

function itemIndex(list: HTMLElement | null, target: EventTarget | null): number {
  const items = [...(list?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  return items.findIndex((item) => item === target);
}

/** The items of one open menu. Shared by the menu bar and the right-click menu. */
export function TerminalMenuList({ id, label, items, onClose, autoFocus = true }: {
  readonly id: string;
  readonly label: string;
  readonly items: readonly TerminalMenuItem[];
  readonly onClose: (returnFocus: boolean) => void;
  readonly autoFocus?: boolean;
}): ReactElement {
  const list = useRef<HTMLDivElement | null>(null);
  const describedBy = useId();
  useEffect(() => {
    if (autoFocus) focusItem(list.current, 0);
  }, [autoFocus]);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = itemIndex(list.current, event.target);
    if (event.key === 'ArrowDown') { focusItem(list.current, index + 1); event.preventDefault(); }
    else if (event.key === 'ArrowUp') { focusItem(list.current, index - 1); event.preventDefault(); }
    else if (event.key === 'Home') { focusItem(list.current, 0); event.preventDefault(); }
    else if (event.key === 'End') { focusItem(list.current, -1); event.preventDefault(); }
    else if (event.key === 'Escape') { onClose(true); event.preventDefault(); event.stopPropagation(); }
    else if (event.key === 'Tab') onClose(false);
  };
  return (
    <div ref={list} id={id} role="menu" aria-label={label} className="terminal-menu-list" onKeyDown={onKeyDown}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className="terminal-menu-item"
          data-menu-item={item.id}
          aria-label={item.ariaLabel ?? item.label}
          aria-disabled={item.disabled || undefined}
          aria-describedby={item.title === undefined ? undefined : `${describedBy}-${item.id}`}
          title={item.title}
          onClick={() => {
            // A disabled item stays focusable so its reason can be read; it just does nothing.
            if (item.disabled) return;
            onClose(true);
            item.onSelect();
          }}
        >
          <span>{item.label}</span>
          {item.shortcut !== undefined && <kbd>{item.shortcut}</kbd>}
          {item.title !== undefined && (
            <span id={`${describedBy}-${item.id}`} className="visually-hidden">{item.title}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function TerminalMenuBar({ label, menus }: {
  /** e.g. "Worker 1 terminal menu". */
  readonly label: string;
  readonly menus: readonly TerminalMenu[];
}): ReactElement {
  const [open, setOpen] = useState<string | null>(null);
  const bar = useRef<HTMLDivElement | null>(null);
  const baseId = useId();

  useEffect(() => {
    if (open === null) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!bar.current?.contains(event.target as Node)) setOpen(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const trigger = (menuId: string) => bar.current?.querySelector<HTMLButtonElement>(`[data-menu="${menuId}"]`) ?? null;
  const move = (from: string, step: number) => {
    const index = menus.findIndex((menu) => menu.id === from);
    const next = menus[(index + step + menus.length) % menus.length];
    if (next === undefined) return;
    setOpen(next.id);
    trigger(next.id)?.focus();
  };

  return (
    <div ref={bar} role="menubar" aria-label={label} className="terminal-menubar">
      {menus.map((menu) => {
        const listId = `${baseId}-${menu.id}`;
        const isOpen = open === menu.id;
        return (
          <div key={menu.id} className="terminal-menu">
            <button
              type="button"
              role="menuitem"
              className="terminal-menu-button"
              data-menu={menu.id}
              aria-haspopup="menu"
              aria-expanded={isOpen}
              aria-controls={isOpen ? listId : undefined}
              onClick={() => setOpen(isOpen ? null : menu.id)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                  setOpen(menu.id);
                  event.preventDefault();
                } else if (event.key === 'ArrowRight') { move(menu.id, 1); event.preventDefault(); }
                else if (event.key === 'ArrowLeft') { move(menu.id, -1); event.preventDefault(); }
                else if (event.key === 'Escape') setOpen(null);
              }}
            >
              {menu.label}
            </button>
            {isOpen && (
              <div
                className="terminal-menu-popover"
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight') { move(menu.id, 1); event.preventDefault(); }
                  else if (event.key === 'ArrowLeft') { move(menu.id, -1); event.preventDefault(); }
                }}
              >
                <TerminalMenuList
                  id={listId}
                  label={`${menu.label} menu`}
                  items={menu.items}
                  onClose={(returnFocus) => {
                    setOpen(null);
                    if (returnFocus) trigger(menu.id)?.focus();
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
